import { db } from '../firebase/config';
import { doc, setDoc, onSnapshot, collection, query, where, Timestamp, getDocs } from 'firebase/firestore';

/**
 * Service für die Verwaltung von Standortaktualisierungen und Echtzeit-Verfolgung
 */
// Spezielle Anpassungen für Produktionsumgebungen
const PROD_ENV = process.env.NODE_ENV === 'production';

// Helper für zuverlässigere Echtzeit-Updates in Produktionsumgebungen
const enhanceProductionReliability = (callback) => {
  return (...args) => {
    // In Produktionsumgebungen zusätzliche Verzögerung für stabilere Verbindungen
    if (PROD_ENV) {
      console.log('LocationService: Produktionsoptimierungen werden angewendet');
      setTimeout(() => callback(...args), 500);
    } else {
      callback(...args);
    }
  };
};

export const LocationService = {
  
  /**
   * Aktualisiert den Standort eines Fahrers in Firestore
   * 
   * @param {string} driverId - ID des Fahrers
   * @param {object} location - Standortdaten { latitude, longitude, heading, speed }
   * @param {string} orderId - Optionale Fahrt-ID für die aktuelle Fahrt
   * @returns {Promise} - Firebase Promise
   */
  updateDriverLocation: async (driverId, location, orderId = null) => {
    if (!driverId || !location) return Promise.reject('Ungültige Parameter');
    
    try {
      const locationData = {
        latitude: location.latitude || location.lat,
        longitude: location.longitude || location.lng,
        heading: location.heading || 0,
        speed: location.speed || 0,
        timestamp: Timestamp.now(),
        ...(orderId ? { orderId } : { orderId: "" })
      };
      
      return await setDoc(doc(db, 'driverLocations', driverId), locationData, { merge: true });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Fahrer-Standorts:', error);
      return Promise.reject(error);
    }
  },
  
  /**
   * Abonniert Standortaktualisierungen von einem bestimmten Fahrer
   * 
   * @param {string} driverId - ID des Fahrers
   * @param {function} callback - Callback-Funktion, die bei jeder Aktualisierung aufgerufen wird
   * @returns {function} - Funktion zum Abbestellen des Listeners
   */
  subscribeToDriverLocation: (driverId, callback) => {
    if (!driverId || !callback) return () => {};
    
    try {
      const unsubscribe = onSnapshot(
        doc(db, 'driverLocations', driverId),
        (doc) => {
          if (doc.exists()) {
            callback(doc.data());
          }
        },
        (error) => {
          console.error('Fehler beim Abonnieren des Fahrer-Standorts:', error);
        }
      );
      
      return unsubscribe;
    } catch (error) {
      console.error('Fehler beim Einrichten des Standort-Listeners:', error);
      return () => {};
    }
  },
  
  /**
   * Abonniert Standortaktualisierungen von allen aktiven Fahrern
   * 
   * @param {function} callback - Callback-Funktion, die bei jeder Aktualisierung aufgerufen wird
   * @returns {function} - Funktion zum Abbestellen des Listeners
   */
  subscribeToAllDriverLocations: (callback) => {
    if (!callback) return () => {};
    
    // Wrapper für zuverlässigere Callbacks in Produktionsumgebungen
    const enhancedCallback = enhanceProductionReliability((locations) => {
      callback(locations);
      
      if (PROD_ENV) {
        console.log('LocationService: Alle Fahrer-Standorte aktualisiert', 
          {timestamp: new Date().toISOString(), count: Object.keys(locations).length});
      }
    });
    
    try {
      // In Produktionsumgebungen robusteres Error-Handling
      const errorHandler = (error) => {
        console.error('Fehler beim Abonnieren aller Fahrer-Standorte:', error);
        
        if (PROD_ENV) {
          console.log('LocationService: Versuche Standort-Listener neu zu starten...');
          // Im Produktionsmodus nach Fehlern neu starten
          setTimeout(() => {
            try {
              const retryUnsubscribe = onSnapshot(
                collection(db, 'driverLocations'),
                enhancedCallback,
                errorHandler
              );
              // Unsubscribe-Funktion überschreiben ist nicht möglich, daher nur Warnung
              console.log('LocationService: Listener erfolgreich neu gestartet');
            } catch (e) {
              console.error('LocationService: Neustart des Listeners fehlgeschlagen:', e);
            }
          }, 2000);
        }
      };
    
      // Verbesserte Konfiguration für Listener
      const unsubscribe = onSnapshot(
        collection(db, 'driverLocations'),
        {
          includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
        },
        enhancedCallback,
        errorHandler
      );
      
      if (PROD_ENV) {
        console.log('LocationService: Alle Fahrer-Standorte-Listener erfolgreich gestartet');
      }
      
      return unsubscribe;
    } catch (error) {
      console.error('Fehler beim Einrichten des Standort-Listeners:', error);
      return () => {};
    }
  },
  
  /**
   * Abonniert Standortaktualisierungen von Fahrern mit bestimmtem Status
   * 
   * @param {string} status - Fahrerstatus ('available', 'busy', 'offline')
   * @param {function} callback - Callback-Funktion, die bei jeder Aktualisierung aufgerufen wird
   * @returns {function} - Funktion zum Abbestellen des Listeners
   */
  subscribeToDriversByStatus: (status, callback) => {
    if (!status || !callback) return () => {};
    
    try {
      // Statusmap - konvertiert UI-Status zu Datenbank-Parametern
      const statusMap = {
        'available': { isAvailable: true, hasActiveRide: false },
        'busy': { isAvailable: true, hasActiveRide: true },
        'offline': { isAvailable: false }
      };
      
      const statusParams = statusMap[status] || {};
      
      // Array für alle Listener, um sauberes Aufräumen zu ermöglichen
      const listeners = [];
      
      if (status === 'busy') {
        // Für beschäftigte Fahrer: Fahrer mit aktiven Fahrten finden
        const activeRidesQuery = query(
          collection(db, 'rides'),
          where('status', 'in', ['accepted', 'in_progress'])
        );
        
        // Listener für aktive Fahrten
        const activeRidesUnsubscribe = onSnapshot(
          activeRidesQuery, 
          (ridesSnapshot) => {
            // Liste der Fahrer-IDs mit aktiven Fahrten erstellen
            const driverIds = [];
            ridesSnapshot.forEach(doc => {
              const rideData = doc.data();
              if (rideData.driverId) {
                driverIds.push(rideData.driverId);
              }
            });
            
            // Keine beschäftigten Fahrer gefunden
            if (driverIds.length === 0) {
              callback({});
              return;
            }
            
            // Standorte dieser Fahrer abrufen
            const locationsUnsubscribe = onSnapshot(
              collection(db, 'driverLocations'),
              (locationsSnapshot) => {
                const locations = {};
                locationsSnapshot.forEach(doc => {
                  if (driverIds.includes(doc.id)) {
                    locations[doc.id] = doc.data();
                  }
                });
                callback(locations);
              }
            );
            
            // Alte Listener-Referenz aufräumen, falls vorhanden
            listeners.forEach((oldListener, index) => {
              if (index > 0) { // Den ersten Listener (activeRidesUnsubscribe) beibehalten
                try {
                  oldListener();
                } catch (e) {
                  console.warn('Fehler beim Aufräumen eines alten Listeners:', e);
                }
                listeners.splice(index, 1);
              }
            });
            
            // Neuen Listener hinzufügen
            listeners.push(locationsUnsubscribe);
          },
          (error) => {
            console.error('Fehler im Fahrten-Listener für beschäftigte Fahrer:', error);
          }
        );
        
        // Listener zur Liste hinzufügen
        listeners.push(activeRidesUnsubscribe);
        
        // Rückgabefunktion für beschäftigte Fahrer
        return () => {
          console.log('Räume alle Listener für Status "busy" auf...');
          listeners.forEach(unsubscribe => {
            try {
              unsubscribe();
            } catch (e) {
              console.warn('Fehler beim Aufräumen eines Listeners:', e);
            }
          });
        };
      } else if (status === 'available') {
        // Für verfügbare Fahrer: Alle verfügbaren Fahrer ohne aktive Fahrt finden
        const driversQuery = query(
          collection(db, 'users'),
          where('isDriver', '==', true),
          where('isAvailable', '==', true)
        );
        
        // Listener für verfügbare Fahrer
        const driversUnsubscribe = onSnapshot(
          driversQuery, 
          async (driversSnapshot) => {
            const driverIds = driversSnapshot.docs.map(doc => doc.id);
            
            if (driverIds.length === 0) {
              callback({});
              return;
            }
            
            // Aktive Fahrten abrufen, um beschäftigte Fahrer auszuschließen
            const activeRidesQuery = query(
              collection(db, 'rides'),
              where('status', 'in', ['accepted', 'in_progress'])
            );
            
            try {
              const activeRidesSnapshot = await getDocs(activeRidesQuery);
              const busyDriverIds = new Set();
              
              activeRidesSnapshot.forEach(doc => {
                const rideData = doc.data();
                if (rideData.driverId) {
                  busyDriverIds.add(rideData.driverId);
                }
              });
              
              // Nur wirklich verfügbare Fahrer (ohne aktive Fahrt) behalten
              const availableDriverIds = driverIds.filter(id => !busyDriverIds.has(id));
              
              if (availableDriverIds.length === 0) {
                callback({});
                return;
              }
              
              // Standorte der verfügbaren Fahrer abrufen
              const locationsUnsubscribe = onSnapshot(
                collection(db, 'driverLocations'),
                (locationsSnapshot) => {
                  const locations = {};
                  locationsSnapshot.forEach(doc => {
                    if (availableDriverIds.includes(doc.id)) {
                      locations[doc.id] = doc.data();
                    }
                  });
                  callback(locations);
                }
              );
              
              // Alte Listener-Referenz aufräumen, falls vorhanden
              listeners.forEach((oldListener, index) => {
                if (index > 0) { // Den ersten Listener (driversUnsubscribe) beibehalten
                  try {
                    oldListener();
                  } catch (e) {
                    console.warn('Fehler beim Aufräumen eines alten Listeners:', e);
                  }
                  listeners.splice(index, 1);
                }
              });
              
              // Neuen Listener hinzufügen
              listeners.push(locationsUnsubscribe);
            } catch (error) {
              console.error('Fehler beim Abrufen aktiver Fahrten:', error);
              callback({});
            }
          },
          (error) => {
            console.error('Fehler im Fahrer-Listener für verfügbare Fahrer:', error);
          }
        );
        
        // Listener zur Liste hinzufügen
        listeners.push(driversUnsubscribe);
        
        // Rückgabefunktion für verfügbare Fahrer
        return () => {
          console.log('Räume alle Listener für Status "available" auf...');
          listeners.forEach(unsubscribe => {
            try {
              unsubscribe();
            } catch (e) {
              console.warn('Fehler beim Aufräumen eines Listeners:', e);
            }
          });
        };
      } else if (status === 'offline') {
        // Für offline Fahrer: Alle Fahrer mit isAvailable=false
        const driversQuery = query(
          collection(db, 'users'),
          where('isDriver', '==', true),
          where('isAvailable', '==', false)
        );
        
        // Listener für offline Fahrer
        const driversUnsubscribe = onSnapshot(
          driversQuery, 
          (driversSnapshot) => {
            const driverIds = driversSnapshot.docs.map(doc => doc.id);
            
            if (driverIds.length === 0) {
              callback({});
              return;
            }
            
            // Standorte der offline Fahrer abrufen
            const locationsUnsubscribe = onSnapshot(
              collection(db, 'driverLocations'),
              (locationsSnapshot) => {
                const locations = {};
                locationsSnapshot.forEach(doc => {
                  if (driverIds.includes(doc.id)) {
                    locations[doc.id] = doc.data();
                  }
                });
                callback(locations);
              }
            );
            
            // Alte Listener-Referenz aufräumen, falls vorhanden
            listeners.forEach((oldListener, index) => {
              if (index > 0) { // Den ersten Listener (driversUnsubscribe) beibehalten
                try {
                  oldListener();
                } catch (e) {
                  console.warn('Fehler beim Aufräumen eines alten Listeners:', e);
                }
                listeners.splice(index, 1);
              }
            });
            
            // Neuen Listener hinzufügen
            listeners.push(locationsUnsubscribe);
          },
          (error) => {
            console.error('Fehler im Fahrer-Listener für offline Fahrer:', error);
          }
        );
        
        // Listener zur Liste hinzufügen
        listeners.push(driversUnsubscribe);
        
        // Rückgabefunktion für offline Fahrer
        return () => {
          console.log('Räume alle Listener für Status "offline" auf...');
          listeners.forEach(unsubscribe => {
            try {
              unsubscribe();
            } catch (e) {
              console.warn('Fehler beim Aufräumen eines Listeners:', e);
            }
          });
        };
      } else {
        // Für alle Fahrer, falls status='all' oder ungültig
        return LocationService.subscribeToAllDriverLocations(callback);
      }
    } catch (error) {
      console.error('Fehler beim Einrichten der Status-Listener:', error);
      return () => {};
    }
  },
  
  /**
   * Berechnet die Entfernung zwischen zwei Punkten in Metern
   * 
   * @param {object} point1 - Erster Punkt { lat, lng } oder { latitude, longitude }
   * @param {object} point2 - Zweiter Punkt { lat, lng } oder { latitude, longitude }
   * @returns {number} - Entfernung in Metern
   */
  calculateDistance: (point1, point2) => {
    // Google Maps API für die Entfernungsberechnung verwenden, wenn verfügbar
    if (window.google && window.google.maps && window.google.maps.geometry) {
      const p1 = new window.google.maps.LatLng(
        point1.latitude || point1.lat,
        point1.longitude || point1.lng
      );
      const p2 = new window.google.maps.LatLng(
        point2.latitude || point2.lat,
        point2.longitude || point2.lng
      );
      
      return window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
    }
    
    // Haversine-Formel als Fallback zur Berechnung der Entfernung
    const R = 6371e3; // Erdradius in Metern
    const lat1 = (point1.latitude || point1.lat) * Math.PI / 180;
    const lat2 = (point2.latitude || point2.lat) * Math.PI / 180;
    const deltaLat = ((point2.latitude || point2.lat) - (point1.latitude || point1.lat)) * Math.PI / 180;
    const deltaLng = ((point2.longitude || point2.lng) - (point1.longitude || point1.lng)) * Math.PI / 180;
    
    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng/2) * Math.sin(deltaLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    return R * c;
  },
  
  /**
   * Findet den nächsten verfügbaren Fahrer zu einem bestimmten Standort
   * 
   * @param {object} location - Standort { latitude, longitude } oder { lat, lng }
   * @param {array} drivers - Array von Fahrern mit Standortinformationen
   * @returns {object} - Der nächste Fahrer oder null
   */
  findNearestDriver: (location, drivers) => {
    if (!location || !drivers || drivers.length === 0) return null;
    
    let nearestDriver = null;
    let shortestDistance = Infinity;
    
    drivers.forEach(driver => {
      if (driver.location && 
          (driver.location.latitude || driver.location.lat) && 
          (driver.location.longitude || driver.location.lng)) {
        
        const distance = LocationService.calculateDistance(location, driver.location);
        
        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearestDriver = { ...driver, distance };
        }
      }
    });
    
    return nearestDriver;
  },
  
  // Interner Speicher für Aufräumarbeiten
  _locationsUnsubscribe: null
};

export default LocationService;